// ═══════════════════════════════════════════════════════════════
// ChecklistEditor — build and edit the inspection checklists a tech
// steps through in the wizard.
//
// A mechanic who never opens this screen still gets the shipped
// standard checklist; this is for shops that want their own wording,
// or several different checklists (a quick oil-change look-over vs a
// full pre-purchase inspection).
//
// Edits are staged locally and only written on Save, so a half-typed
// section can't reach a tech mid-inspection.
// ═══════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import {
  ClipboardCheck, Plus, Trash2, Save, X, CircleDot, Copy,
} from 'lucide-react';
import { useChecklists } from './lib/use-checklists.js';
import {
  blankChecklist, cloneDefaultChecklist, normalizeChecklist,
} from './lib/inspection-template.js';
import { shortId } from './lib/job-helpers.js';
import ConfirmDialog from './ConfirmDialog.jsx';

function SectionEditor({ section, onPatch, onRemove }) {
  const isTires = section.kind === 'tires';

  return (
    <div className="cl-section">
      <div className="cl-section-head">
        <input
          className="cl-section-name"
          value={section.label}
          maxLength={60}
          onChange={(e) => onPatch({ label: e.target.value })}
          placeholder="Section name"
          aria-label="Section name"
        />
        <button
          type="button"
          className="btn-link danger"
          onClick={onRemove}
          aria-label={`Remove section ${section.label}`}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {isTires ? (
        <p className="muted small cl-tire-note">
          <CircleDot size={12} /> Tire grid — per-wheel tread depth, pressure, and wear.
          Its layout is fixed, so there are no items to edit here.
        </p>
      ) : (
        <>
          <ul className="cl-item-list">
            {section.items.map((item) => (
              <li key={item.key} className="cl-item-row">
                <input
                  className="cl-item-input"
                  value={item.label}
                  maxLength={80}
                  placeholder="Item name"
                  aria-label="Item name"
                  onChange={(e) =>
                    onPatch({
                      items: section.items.map((i) =>
                        i.key === item.key ? { ...i, label: e.target.value } : i
                      ),
                    })
                  }
                />
                <button
                  type="button"
                  className="btn-link danger"
                  aria-label={`Remove ${item.label || 'item'}`}
                  onClick={() =>
                    onPatch({ items: section.items.filter((i) => i.key !== item.key) })
                  }
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="btn-link"
            onClick={() =>
              onPatch({ items: [...section.items, { key: shortId(), label: '' }] })
            }
          >
            <Plus size={13} /> Add item
          </button>
        </>
      )}
    </div>
  );
}

function ChecklistForm({ initial, onSave, onCancel, saving }) {
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState(null);

  function patchSection(key, patch) {
    setDraft((d) => ({
      ...d,
      sections: d.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)),
    }));
  }

  const hasTires = draft.sections.some((s) => s.kind === 'tires');

  function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    const clean = normalizeChecklist(draft);
    // normalizeChecklist drops blank items and empty sections, so an
    // all-blank checklist arrives here with nothing in it.
    if (!clean || clean.sections.length === 0) {
      setError('Add at least one section with a named item before saving.');
      return;
    }
    onSave(clean).catch((err) => setError(err.message));
  }

  return (
    <form className="cl-form" onSubmit={handleSubmit}>
      <div className="form-row">
        <span>Checklist name</span>
        <input
          value={draft.name}
          maxLength={60}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          placeholder="e.g. Full vehicle inspection"
        />
      </div>

      {draft.sections.map((s) => (
        <SectionEditor
          key={s.key}
          section={s}
          onPatch={(patch) => patchSection(s.key, patch)}
          onRemove={() =>
            setDraft((d) => ({ ...d, sections: d.sections.filter((x) => x.key !== s.key) }))
          }
        />
      ))}

      <div className="cl-add-row">
        <button
          type="button"
          className="btn-link"
          onClick={() =>
            setDraft((d) => ({
              ...d,
              sections: [
                ...d.sections,
                {
                  key: shortId(),
                  label: `Section ${d.sections.length + 1}`,
                  kind: 'status',
                  items: [{ key: shortId(), label: '' }],
                },
              ],
            }))
          }
        >
          <Plus size={13} /> Add section
        </button>
        {!hasTires && (
          <button
            type="button"
            className="btn-link"
            onClick={() =>
              setDraft((d) => ({
                ...d,
                sections: [
                  ...d.sections,
                  { key: shortId(), label: 'Tires', kind: 'tires', items: [] },
                ],
              }))
            }
          >
            <Plus size={13} /> Add tire grid
          </button>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          <X size={15} /> Cancel
        </button>
        <button type="submit" className="btn" disabled={saving}>
          <Save size={15} strokeWidth={2.5} /> {saving ? 'Saving…' : 'Save checklist'}
        </button>
      </div>
    </form>
  );
}

export default function ChecklistEditor() {
  const { checklists, usingDefault, ready, saveChecklist, deleteChecklist } = useChecklists();
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [error, setError] = useState(null);

  async function handleSave(clean) {
    setSaving(true);
    try {
      await saveChecklist(clean);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(c) {
    setError(null);
    try {
      await deleteChecklist(c.id);
    } catch (e) {
      setError(e.message);
    }
  }

  return (
    <section className="settings-section settings-section-standalone">
      <div className="settings-section-head">
        <ClipboardCheck size={16} />
        <div>
          <h2>Inspection checklists</h2>
          <p className="muted small">
            What a tech steps through in the inspection wizard. Set up as many as you
            like — a quick look-over and a full inspection can be separate checklists.
          </p>
        </div>
      </div>

      {!ready && <p className="muted small">Loading…</p>}
      {error && <p className="form-error">{error}</p>}

      {ready && !editing && (
        <>
          {usingDefault && (
            <p className="muted small cl-default-note">
              You're using the built-in standard checklist. Save a copy to start
              customizing it — the built-in stays available until you do.
            </p>
          )}

          <ul className="cl-list">
            {checklists.map((c) => (
              <li key={c.id} className="cl-list-row">
                <div className="cl-list-body">
                  <strong>{c.name}</strong>
                  <span className="muted small">
                    {c.sections.length} section{c.sections.length === 1 ? '' : 's'}
                    {' · '}
                    {c.sections.reduce((n, s) => n + s.items.length, 0)} items
                    {c.sections.some((s) => s.kind === 'tires') && ' · tire grid'}
                  </span>
                </div>
                <div className="cl-list-actions">
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setEditing(usingDefault ? { ...c, id: shortId() } : c)}
                  >
                    {usingDefault ? <><Copy size={13} /> Save a copy</> : 'Edit'}
                  </button>
                  {!usingDefault && (
                    <button
                      type="button"
                      className="btn-link danger"
                      onClick={() => setPendingDelete(c)}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <div className="cl-add-row">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setEditing(blankChecklist())}
            >
              <Plus size={15} /> New checklist
            </button>
            {!usingDefault && (
              <button
                type="button"
                className="btn-link"
                onClick={() => setEditing({ ...cloneDefaultChecklist(), id: shortId() })}
              >
                <Copy size={13} /> Add a copy of the standard checklist
              </button>
            )}
          </div>
        </>
      )}

      {editing && (
        <ChecklistForm
          initial={editing}
          saving={saving}
          onSave={handleSave}
          onCancel={() => setEditing(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title={`Delete "${pendingDelete.name}"?`}
          body={
            'Inspections already done with this checklist keep their own copy of it, ' +
            'so past reports are unaffected. It just stops being offered for new ones.'
          }
          confirmLabel="Delete checklist"
          cancelLabel="Cancel"
          onConfirm={() => {
            const c = pendingDelete;
            setPendingDelete(null);
            handleDelete(c);
          }}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </section>
  );
}
